import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { AgentMission, AgentMissionHarness, AgentRun } from '../../api/schema'
import { useLocale } from '../../contexts/LocaleContext'
import { Button } from '../ui/button'
import { Checkbox } from '../ui/checkbox'
import { Input } from '../ui/input'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog'
import { launchAgentRun, type ApiError, type TargetEntityType } from './agent-runs-api'
import { pairLabel } from './AgentRunCard'

/** The launch errors with their own message (spec 2026-10-21-19 §2). */
export const LAUNCH_ERROR_CODES = [
    'AGENT_TARGET_INVALID',
    'AGENT_PROVIDER_NOT_ALLOWED',
    'AGENT_DIRECT_NEEDS_INTERNAL',
    'AGENT_RUN_ACTIVE',
    'AGENT_DAILY_CAP_REACHED',
] as const

export type ParamField =
    | { name: string; kind: 'enum'; options: string[]; initial: string }
    | { name: string; kind: 'boolean'; initial: boolean }
    | { name: string; kind: 'number'; integer: boolean; initial: number | '' }
    | { name: string; kind: 'text'; initial: string }

/** paramFields: one field per top-level property of a params JSON Schema. */
export function paramFields(schema: Record<string, unknown> | undefined): ParamField[] {
    const props = (schema?.properties ?? {}) as Record<string, Record<string, unknown>>
    return Object.entries(props).map(([name, prop]): ParamField => {
        const def = prop.default
        if (prop.type === 'string' && Array.isArray(prop.enum)) {
            const options = prop.enum.map(String)
            return { name, kind: 'enum', options, initial: typeof def === 'string' ? def : options[0] ?? '' }
        }
        if (prop.type === 'boolean') return { name, kind: 'boolean', initial: def === true }
        if (prop.type === 'integer' || prop.type === 'number') {
            return { name, kind: 'number', integer: prop.type === 'integer', initial: typeof def === 'number' ? def : '' }
        }
        return { name, kind: 'text', initial: typeof def === 'string' ? def : '' }
    })
}

export const pairKey = (h: Pick<AgentMissionHarness, 'harness' | 'model'>) => `${h.harness}::${h.model}`

/** defaultPair: the harness marked default, else the first. */
export function defaultPair(mission: AgentMission | undefined): AgentMissionHarness | undefined {
    if (!mission) return undefined
    return mission.harnesses.find((h) => h.default) ?? mission.harnesses[0]
}

/** launchErrorMessage: one message per known code, the API title otherwise. */
export function launchErrorMessage(t: (key: string, opts?: Record<string, unknown>) => string, err: ApiError): string {
    // A plan mission on a target without a plan (spec 2026-10-21-20 §6).
    if (err.fields?.some((f) => f.name === 'target_entity_uid' && f.message === 'no_plan')) return t('errors.no_plan')
    // An enrich-site import whose extraction is not done (spec 2026-10-21-21 §9).
    if (err.fields?.some((f) => f.name === 'target_entity_uid' && f.message === 'extraction_not_done')) return t('errors.extraction_not_done')
    if (err.code && (LAUNCH_ERROR_CODES as readonly string[]).includes(err.code)) return t(`launch.errors.${err.code}`)
    return err.title || t('launch.errors.generic')
}

export function LaunchAgentRunDialog({
    org,
    open,
    onOpenChange,
    missions,
    writeMode,
    targetEntityType,
    targetUid,
    onLaunched,
}: {
    org: string
    open: boolean
    onOpenChange: (open: boolean) => void
    missions: AgentMission[]
    writeMode: 'propose' | 'direct'
    targetEntityType: TargetEntityType
    targetUid: string
    onLaunched: (run: AgentRun) => void
}) {
    const { t } = useTranslation('agent-runs')
    const { locale } = useLocale()
    const [missionCode, setMissionCode] = useState(missions[0]?.code ?? '')
    const mission = missions.find((m) => m.code === missionCode) ?? missions[0]
    const [pair, setPair] = useState(() => {
        const p = defaultPair(mission)
        return p ? pairKey(p) : ''
    })
    const fields = useMemo(() => paramFields(mission?.params_schema as Record<string, unknown> | undefined), [mission])
    const [values, setValues] = useState<Record<string, unknown>>({})
    const [submitting, setSubmitting] = useState(false)
    const [error, setError] = useState<string | undefined>()
    const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({})

    // A new mission resets the pair to its default and the params to the schema's.
    useEffect(() => {
        const p = defaultPair(mission)
        setPair(p ? pairKey(p) : '')
        setValues(Object.fromEntries(fields.map((f) => [f.name, f.initial])))
        setError(undefined)
        setFieldErrors({})
    }, [mission, fields])

    const selected = mission?.harnesses.find((h) => pairKey(h) === pair)
    const stats = selected?.stats
    // One decimal: "1.2M tokens in" reads better than "1M" in a median.
    const num = (n: number) => new Intl.NumberFormat(locale, { notation: 'compact', maximumFractionDigits: 1 }).format(n)

    const submit = async () => {
        if (!mission || !selected) return
        setSubmitting(true)
        setError(undefined)
        setFieldErrors({})
        const params: Record<string, unknown> = {}
        for (const f of fields) {
            const v = values[f.name]
            if (f.kind === 'number' && (v === '' || v === undefined)) continue
            params[f.name] = v
        }
        const { run, error: err } = await launchAgentRun(org, {
            mission: mission.code,
            target_entity_type: targetEntityType,
            target_entity_uid: targetUid,
            harness: selected.harness,
            model: selected.model,
            params,
        })
        setSubmitting(false)
        if (run) {
            onLaunched(run)
            onOpenChange(false)
            return
        }
        if (err?.fields?.length && !(err.code && (LAUNCH_ERROR_CODES as readonly string[]).includes(err.code))) {
            setFieldErrors(Object.fromEntries(err.fields.map((f) => [f.name.replace(/^params[/.]?/, ''), f.message])))
        }
        setError(launchErrorMessage(t, err ?? {}))
    }

    const paramLabel = (name: string) => t(`missions.${mission?.code}.params.${name}.label`, { defaultValue: name })
    const optionLabel = (name: string, value: string) =>
        t(`missions.${mission?.code}.params.${name}.options.${value}`, { defaultValue: value })
    const selectClass =
        'block w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground shadow-sm focus:border-ring focus:outline-none focus:ring-2 focus:ring-ring'

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="agent-launch-dialog">
                <DialogHeader>
                    <DialogTitle>{missions.length > 1 ? t('launch.title_many') : t(mission?.title_key ?? '', { defaultValue: mission?.code })}</DialogTitle>
                    {mission && <DialogDescription>{t(mission.description_key, { defaultValue: '' })}</DialogDescription>}
                </DialogHeader>

                <div className="space-y-4">
                    {missions.length > 1 && (
                        <label className="block space-y-1 text-sm">
                            <span className="font-medium text-foreground">{t('launch.mission')}</span>
                            <select
                                className={selectClass}
                                value={mission?.code}
                                onChange={(e) => setMissionCode(e.target.value)}
                                data-testid="agent-launch-mission"
                            >
                                {missions.map((m) => (
                                    <option key={m.code} value={m.code} data-testid={`agent-launch-mission-option-${m.code}`}>
                                        {t(m.title_key, { defaultValue: m.code })}
                                    </option>
                                ))}
                            </select>
                        </label>
                    )}

                    <label className="block space-y-1 text-sm">
                        <span className="font-medium text-foreground">{t('launch.pair')}</span>
                        <select className={selectClass} value={pair} onChange={(e) => setPair(e.target.value)} data-testid="agent-launch-pair">
                            {(mission?.harnesses ?? []).map((h) => (
                                <option key={pairKey(h)} value={pairKey(h)} data-testid={`agent-launch-pair-option-${h.harness}-${h.model}`}>
                                    {pairLabel(t, h.harness, h.model)}
                                </option>
                            ))}
                        </select>
                        {selected && (
                            <span className="block text-xs text-muted-foreground" data-testid="agent-launch-billing">
                                {selected.billing === 'per_token' ? t('launch.billing_per_token') : t('launch.billing_subscription')}
                            </span>
                        )}
                    </label>

                    <ul className="space-y-1 text-sm text-muted-foreground">
                        {mission && (
                            <li data-testid="agent-launch-timeout">{t('launch.timeout', { minutes: mission.timeout_minutes })}</li>
                        )}
                        <li data-testid="agent-launch-stats">
                            {stats
                                ? t('launch.stats', {
                                    count: stats.runs,
                                    minutes: Math.max(1, Math.round(stats.median_duration_s / 60)),
                                    input: num(stats.median_input_tokens),
                                    output: num(stats.median_output_tokens),
                                })
                                : t('launch.stats_none')}
                        </li>
                        <li data-testid="agent-launch-write-mode">
                            {writeMode === 'direct' ? t('launch.write_mode_direct') : t('launch.write_mode_propose')}
                        </li>
                    </ul>

                    {fields.map((f) => {
                        const testid = `agent-launch-param-${f.name}`
                        const err = fieldErrors[f.name] ?? fieldErrors[`/${f.name}`]
                        return (
                            <div key={f.name} className="space-y-1 text-sm">
                                {f.kind === 'boolean' ? (
                                    <label className="flex items-center gap-2">
                                        <Checkbox
                                            checked={values[f.name] === true}
                                            onCheckedChange={(c) => setValues((v) => ({ ...v, [f.name]: c }))}
                                            data-testid={testid}
                                        />
                                        <span className="font-medium text-foreground">{paramLabel(f.name)}</span>
                                    </label>
                                ) : (
                                    <label className="block space-y-1">
                                        <span className="font-medium text-foreground">{paramLabel(f.name)}</span>
                                        {f.kind === 'enum' ? (
                                            <select
                                                className={selectClass}
                                                value={String(values[f.name] ?? f.initial)}
                                                onChange={(e) => setValues((v) => ({ ...v, [f.name]: e.target.value }))}
                                                data-testid={testid}
                                            >
                                                {f.options.map((o) => (
                                                    <option key={o} value={o}>{optionLabel(f.name, o)}</option>
                                                ))}
                                            </select>
                                        ) : (
                                            <Input
                                                type={f.kind === 'number' ? 'number' : 'text'}
                                                step={f.kind === 'number' && f.integer ? 1 : undefined}
                                                value={String(values[f.name] ?? f.initial)}
                                                onChange={(e) => {
                                                    const raw = e.target.value
                                                    setValues((v) => ({
                                                        ...v,
                                                        [f.name]: f.kind === 'number' ? (raw === '' ? '' : Number(raw)) : raw,
                                                    }))
                                                }}
                                                aria-invalid={!!err}
                                                data-testid={testid}
                                            />
                                        )}
                                    </label>
                                )}
                                {err && <span className="block text-xs text-destructive" data-testid={`${testid}-error`}>{err}</span>}
                            </div>
                        )
                    })}

                    {error && (
                        <div className="rounded-md border border-destructive/25 bg-destructive/10 px-3 py-2 text-sm text-destructive" data-testid="agent-launch-error">
                            {error}
                        </div>
                    )}
                </div>

                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="agent-launch-cancel">
                        {t('launch.cancel')}
                    </Button>
                    <Button onClick={() => void submit()} disabled={submitting || !selected} data-testid="agent-launch-submit">
                        {t('launch.submit')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
